/**
 * chartDataBuilder - 共享的图表数据构建工具
 * @description Games 和 TopPublishersV2 复用的数据聚合、格式化逻辑
 */
import { formatDateStr } from '@intelligence/v3/utils/date';
import { formatKMBWithPlaceholder } from '@intelligence/v3/utils/number';
import {
  createLineChartTooltipFormatter,
  renderBarChartTooltip,
  renderPieChartTooltip,
} from '@intelligence/v3/components/ChartComponents';
import { GRANULARITY_MOMENT_HELPER_MAPPING } from '@intelligence/utils/dateUtils';

interface IChartDataPayload {
  iconUrl?: string;
  iconAlt?: string;
  [key: string]: any;
}

// ========== Bar/Pie 聚合数据 ==========

interface IAggregateItem {
  id: string;
  name: string;
  value: number;
  payload: IChartDataPayload;
}

interface IBuildAggregatedChartDataParams<T> {
  sortedData: T[];
  checkedIds: string[];
  maxItems: number;
  getId: (item: T) => string;
  getName: (item: T) => string;
  getValue: (item: T) => number;
  getIconUrl: (item: T) => string;
}

const getNumberValueBuilder = (getter: Function): (item: any) => number => {
  return (item: any) => {
    const itemValue = getter?.(item);
    const numValue = Number.parseFloat(itemValue);
    if (Number.isNaN(numValue)) {
      return 0;
    }

    return numValue;
  };
};

/**
 * 构建 bar/pie 图的聚合数据（含 Others 归类）
 */
export function buildAggregatedChartData<T>(params: IBuildAggregatedChartDataParams<T>): IAggregateItem[] {
  const { sortedData, checkedIds, maxItems, getId, getName, getValue: getValueOutter, getIconUrl } = params;
  const getValue = getNumberValueBuilder(getValueOutter);

  if (!checkedIds?.length || !sortedData?.length) {
    return [];
  }

  const shouldAggregateOthers = sortedData.length > maxItems;
  const checkedIdSet = new Set(checkedIds);

  const selectedItems = sortedData
    .filter((item) => checkedIdSet.has(getId(item)))
    .map((item) => {
      const id = getId(item);
      const name = getName(item) || id;
      return {
        id,
        name,
        value: getValue(item),
        payload: {
          iconUrl: getIconUrl(item) || '',
          iconAlt: name,
        },
      };
    });

  const hasOthers = sortedData.some((item) => !checkedIdSet.has(getId(item)));

  if (!shouldAggregateOthers || !hasOthers) {
    return selectedItems;
  }

  const othersValue = sortedData.reduce((sum, item) => {
    if (checkedIdSet.has(getId(item))) {
      return sum;
    }
    return sum + (getValue(item) ?? 0);
  }, 0);

  return [
    ...selectedItems,
    {
      id: 'others',
      name: 'Others',
      value: othersValue,
      payload: {},
    },
  ];
}

// ========== Line 折线图数据 ==========

interface ILineChartResultItem {
  date: string;
  value: number;
  name: string;
  entityId: string;
  payload: IChartDataPayload;
}

interface IBuildLineChartDataParams<T> {
  rawData: T[];
  checkedIds: string[];
  totalCount: number;
  maxItems: number;
  getDate: (item: T) => string;
  getEntityId: (item: T) => string;
  getName: (item: T) => string;
  getValue: (item: T) => number;
  getIconUrl: (item: T) => string;
}

/**
 * 构建折线图数据（含 Others 聚合）
 * @returns 扁平数组，每条记录为 (date, entity) 对
 */
export function buildLineChartData<T>(params: IBuildLineChartDataParams<T>): ILineChartResultItem[] {
  const { rawData, checkedIds, totalCount, maxItems, getDate, getEntityId, getName, getValue, getIconUrl } = params;

  if (!rawData?.length || !checkedIds?.length) {
    return [];
  }

  const shouldAggregateOthers = totalCount > maxItems;
  const targetIdSet = new Set(checkedIds);
  const nameMap: Record<string, string> = {};
  const iconMap: Record<string, string> = {};
  const dateGroupMap = new Map<string, Map<string, T>>();

  rawData.forEach((item) => {
    const entityId = getEntityId(item) || '';
    if (!nameMap[entityId]) {
      nameMap[entityId] = getName(item);
    }
    if (!iconMap[entityId]) {
      iconMap[entityId] = getIconUrl(item) || '';
    }
    let entityMap = dateGroupMap.get(getDate(item));
    if (!entityMap) {
      entityMap = new Map();
      dateGroupMap.set(getDate(item), entityMap);
    }
    entityMap.set(entityId, item);
  });

  const dates = Array.from(new Set(rawData.map(getDate))).sort();
  const result: ILineChartResultItem[] = [];

  dates.forEach((date) => {
    const entityMap = dateGroupMap.get(date);

    checkedIds.forEach((entityId) => {
      const item = entityMap?.get(entityId);
      result.push({
        date,
        value: item ? getValue(item) : 0,
        name: nameMap[entityId] || entityId,
        entityId,
        payload: {
          iconUrl: iconMap[entityId] || '',
          iconAlt: nameMap[entityId] || entityId,
        },
      });
    });

    if (shouldAggregateOthers) {
      let othersValue = 0;
      entityMap?.forEach((item, entityId) => {
        if (!targetIdSet.has(entityId)) {
          othersValue += getValue(item) || 0;
        }
      });
      result.push({
        date,
        value: othersValue,
        name: 'Others',
        entityId: 'others',
        payload: {},
      });
    }
  });

  return result;
}

// ========== 格式化工具 ==========

export interface IMetricNameI18nMap {
  daily: Record<string, string>;
  monthly: Record<string, string>;
}

/**
 * 创建指标名称格式化函数
 */
export function createMetricNameFormatter(
  granularity: string,
  i18nMap: IMetricNameI18nMap,
): (metricKey: string) => string {
  return (metricKey: string): string => {
    const map = granularity === 'monthly' ? i18nMap.monthly : i18nMap.daily;
    return map[metricKey] || '';
  };
}

/**
 * 创建 x 轴标签格式化函数
 */
export function createXAxisLabelFormatter(granularity: string): (value: string) => string {
  return (value: string) => {
    const dateFormat = GRANULARITY_MOMENT_HELPER_MAPPING[granularity]?.format || 'YYYY-MM-DD';
    return formatDateStr(value, dateFormat);
  };
}

/**
 * 创建图表 tooltip formatter 集合
 */
interface ICreateTooltipFormattersParams {
  getMetricName: (metricKey: string) => string;
  metricTab: string;
  xAxisLabelFormatter: (value: string) => string;
  timeRange?: string;
}

export function createChartTooltipFormatters(params: ICreateTooltipFormattersParams) {
  const { getMetricName, metricTab, xAxisLabelFormatter, timeRange } = params;

  const lineTooltipFormatter = createLineChartTooltipFormatter({
    metricName: getMetricName(metricTab),
    xAxisLabelFormatter,
  });

  const barTooltipFormatter = (chartParams: any[]) => {
    const firstParam = Array.isArray(chartParams) ? chartParams[0] : chartParams;
    if (!firstParam) {
      return '';
    }

    return renderBarChartTooltip({
      timeRange,
      metricName: getMetricName(metricTab),
      dataList: [
        {
          color: firstParam.color || '#999',
          name: firstParam.name || '',
          value: formatKMBWithPlaceholder(firstParam.value),
          iconUrl: firstParam.data?.payload?.iconUrl || '',
          iconAlt: firstParam.data?.payload?.iconAlt || firstParam.name || '',
        },
      ],
    });
  };

  const pieTooltipFormatter = (chartParam: any) => {
    if (!chartParam) {
      return '';
    }

    return renderPieChartTooltip({
      metricName: getMetricName(metricTab),
      timeRange,
      dataItem: {
        color: chartParam.color || '#999',
        name: chartParam.name || '',
        value: formatKMBWithPlaceholder(chartParam.value),
        percent: `${(chartParam.percent || 0).toFixed(1)}%`,
        iconUrl: chartParam.data?.payload?.iconUrl || '',
        iconAlt: chartParam.data?.payload?.iconAlt || chartParam.name || '',
      },
    });
  };

  return { lineTooltipFormatter, barTooltipFormatter, pieTooltipFormatter };
}
